import { type ProductizedServiceDeliverables_productizedService$key } from '#app/__generated__/relay/ProductizedServiceDeliverables_productizedService.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Deliverables = styled.ul`
  list-style: auto;
`;

const Deliverable = styled.li`
  ${({ theme }) => css`
    list-style-type: disc;
    display: list-item;
    margin-left: 16px;

    &::marker {
      color: ${theme.colorsV2.gray70};
    }

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    > *:first-child {
      margin-bottom: 6px;
    }
  `}
`;

const DeliverableDescription = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray70};
  `}
`;

type ProductizedServiceDeliverablesProps = {
  readonly productizedServiceRef: ProductizedServiceDeliverables_productizedService$key;
};

export const ProductizedServiceDeliverables = ({
  productizedServiceRef,
}: ProductizedServiceDeliverablesProps) => {
  const isMobile = useIsMobile();
  const {
    deliverables: { edges },
  } = useFragment<ProductizedServiceDeliverables_productizedService$key>(
    graphql`
      fragment ProductizedServiceDeliverables_productizedService on ProductizedService {
        deliverables {
          edges {
            node {
              id
              description
              title
            }
          }
        }
      }
    `,
    productizedServiceRef,
  );

  const deliverables = edges.map(({ node: { description, id, title } }) => ({
    description,
    id,
    title,
  }));

  if (deliverables.length === 0) {
    return null;
  }

  return (
    <Container>
      {isMobile && <Text textStyle="overline">What's included</Text>}
      <Deliverables>
        {deliverables.map(({ description, id, title }) => (
          <Deliverable key={id}>
            <Text textStyle="subtitleRegular">{title}</Text>
            <DeliverableDescription textStyle="bodyRegular">
              {description}
            </DeliverableDescription>
          </Deliverable>
        ))}
      </Deliverables>
    </Container>
  );
};
